"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import Logo from '@/components/Logo';
import SearchOverlay from '@/components/SearchOverlay';
import { SHOW_TEMPLATES } from '@/lib/feature-flags';
import { Button } from '@/components/ui/button';
import { 
  Sheet, 
  SheetContent, 
  SheetTrigger, 
  SheetHeader, 
  SheetTitle,
  SheetDescription,
  SheetClose
} from '@/components/ui/sheet';

const navLinks = [
  { label: 'Services', href: '/services' },
  { label: 'Portfolio', href: '/portfolio' },
  ...(SHOW_TEMPLATES ? [{ label: 'Templates', href: '/templates' }] : []),
  { label: 'Blog', href: '/blog' },
  { label: 'About', href: '/about' },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();
  const isHome = pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const transparent = isHome && !scrolled && !mobileOpen;

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
          transparent
            ? 'bg-transparent'
            : 'bg-white/95 backdrop-blur-md shadow-sm border-b border-bloom-border/40'
        )}
      >
        <nav className="container mx-auto flex items-center justify-between px-6 py-4" aria-label="Main navigation">
          <Link href="/" className="focus-bloom rounded-md" aria-label="Laurel Web Co. home">
            <Logo variant="dark" className="h-14 w-auto" />
          </Link>

          <ul className="hidden lg:flex items-center gap-10" role="list">
            {navLinks.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className={cn(
                    'label-text !text-[11px] transition-all hover:text-bloom-green-deep focus-bloom rounded-md',
                    transparent ? '!text-bloom-charcoal/80' : '!text-bloom-charcoal',
                    pathname.startsWith(link.href) && '!text-bloom-green-deep font-bold'
                  )}
                  aria-current={pathname.startsWith(link.href) ? 'page' : undefined}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="hidden lg:flex items-center gap-6">
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 rounded-full text-bloom-charcoal hover:bg-bloom-cream transition-colors focus-bloom"
              aria-label="Open search"
            >
              <Search size={18} aria-hidden="true" />
            </button>
            <Button asChild size="sm">
              <Link href="/contact">Contact Us</Link>
            </Button>
          </div>

          <div className="lg:hidden flex items-center gap-4">
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 text-bloom-charcoal focus-bloom rounded-md"
              aria-label="Open search"
            >
              <Search size={22} />
            </button>
            
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  className="focus-bloom rounded-md p-2 text-bloom-charcoal"
                  aria-label="Open navigation menu"
                >
                  <Menu size={26} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[320px] sm:w-[400px] p-0 border-l border-bloom-border bg-white shadow-2xl [&>button]:hidden">
                <SheetHeader className="sr-only">
                  <SheetTitle>Navigation Menu</SheetTitle>
                  <SheetDescription>Main site links and secondary actions.</SheetDescription>
                </SheetHeader>
                <div className="flex flex-col h-full relative">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-bloom-border min-h-[72px]">
                    <Link href="/" onClick={() => setMobileOpen(false)} className="focus-bloom">
                      <Logo variant="dark" className="h-14 w-auto" />
                    </Link>
                    <SheetClose className="focus-bloom rounded-md p-2 text-bloom-charcoal -mr-2">
                      <X size={26} />
                    </SheetClose>
                  </div>

                  <div className="flex-1 overflow-y-auto px-6 py-8">
                    <nav className="flex flex-col gap-2">
                      {navLinks.map((link) => (
                        <Link
                          key={link.href}
                          href={link.href}
                          onClick={() => setMobileOpen(false)}
                          className={cn(
                            "font-sans font-bold text-lg px-4 py-4 rounded-xl transition-all",
                            pathname.startsWith(link.href) 
                              ? "text-bloom-green-deep bg-bloom-green-deep/5" 
                              : "text-bloom-charcoal hover:bg-bloom-cream/50 active:scale-95"
                          )}
                        >
                          {link.label}
                        </Link>
                      ))}


                      <div className="mt-8 pt-8">
                        <Button asChild className="w-full h-14 rounded-2xl shadow-xl">
                          <Link href="/contact" onClick={() => setMobileOpen(false)}>Contact Us</Link>
                        </Button>
                      </div>
                    </nav>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </header>

      <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};
